import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { NgForm } from '@angular/forms';
import {
    HOME_PHONE_NUMBER_REGEX,
    isNullOrWhiteSpace,
    MOBILE_PHONE_NUMBER_REGEX
} from '@classifieds-sell/shared';
import { UserFacade } from '@classifieds-sell/state';
import { NotificationService } from '@classifieds-sell/trademe-core-ui';
import { DelayService } from '@trademe/tangram';
import { from, Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'tm-contact-modal',
    templateUrl: './tm-contact-modal.component.html',
    styleUrls: ['tm-contact-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TmContactModalComponent implements OnInit, OnDestroy {
    _destroyed$: Subject<void> = new Subject<void>();
    homePhoneNumberRegex: string = HOME_PHONE_NUMBER_REGEX;
    mobilePhoneNumberRegex: string = MOBILE_PHONE_NUMBER_REGEX;
    hasErrorInContactModal = false;
    isFormReset = false;
    previousMobileNumber: string;
    public mobileNumber: string;
    previousLandlineNumber: string;
    public landlineNumber: string;

    @Input() mobilePhoneNumberValidator: any;
    @Input() landlineNumberValidator: any;
    @Output() closeModal: EventEmitter<boolean> = new EventEmitter();

    @ViewChild('contactUpdateDetailsForm', { static: true })
    contactUpdateDetailsForm: NgForm;

    constructor(
        private _userFacade: UserFacade,
        private _notificationService: NotificationService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _delayService: DelayService
    ) {}

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        this.handleUpdateUserDetailsResponse();
        from(this._userFacade.memberSummary$)
            .pipe(takeUntil(this._destroyed$))
            .subscribe((memberSummary) => {
                this.mobileNumber = memberSummary.mobilePhoneNumber;
                this.previousMobileNumber =
                    memberSummary.mobilePhoneNumber.replace(' ', '');
                this.landlineNumber = memberSummary.landlinePhoneNumber;
                this.previousLandlineNumber =
                    memberSummary.landlinePhoneNumber.replace(' ', '');
            });
    }

    updateContactNumbers(): void {
        if (
            isNullOrWhiteSpace(this.mobileNumber) &&
            isNullOrWhiteSpace(this.landlineNumber)
        ) {
            this.hasErrorInContactModal = true;
        } else if (this.contactUpdateDetailsForm.valid) {
            this._userFacade.updateUserDetails({
                mobilePhoneNumber: this.mobileNumber,
                homePhoneNumber: this.landlineNumber
            });
        }
    }

    handleUpdateUserDetailsResponse(): void {
        from(this._userFacade.updateUserDetailsResponse$)
            .pipe(takeUntil(this._destroyed$), skip(1)) // ignore past/existing state
            .subscribe((listingResponse) => {
                if (listingResponse.success) {
                    this._delayService
                        .createDelay(2000)
                        .subscribe(() => this._userFacade.reloadUserDetails());

                    this._notificationService.success({
                        title: 'Contact details',
                        message: 'Your contact details have been updated.'
                    });
                } else {
                    this._notificationService.warning({
                        title: 'Contact details',
                        message: 'Your contact details could not be updated.'
                    });
                }
                this.closeModal.emit(true);
            });
    }

    onContactDetailChange() {
        this.hasErrorInContactModal = false;
        if (
            isNullOrWhiteSpace(this.mobileNumber) &&
            isNullOrWhiteSpace(this.landlineNumber)
        ) {
            this.hasErrorInContactModal = true;
        }
        this.isFormReset =
            this.mobileNumber === this.previousMobileNumber &&
            this.landlineNumber === this.previousLandlineNumber;
        this._changeDetectorRef.markForCheck();
    }

    public onClose(): void {
        this.closeModal.emit();
    }
}
